import { useEffect, useState } from 'react';
import { documents } from '../api/endpoints';
import { apiGet } from '../api/client';

/** Элемент для просмотра: id blob'а + отображаемое имя. */
export interface PreviewItem {
    blobId: string;
    name: string;
}

type Descriptor = Awaited<ReturnType<typeof documents.filePreview>>;

/**
 * Модальное окно просмотра вложения поверх текущей страницы.
 *
 * pdf — браузерный просмотр (страницы/масштаб), image — масштаб кликом,
 * txt — текст с переносами, docx/xlsx — серверная конвертация в HTML
 * (для xlsx — выбор листа). Неподдерживаемое/ошибка — сообщение +
 * «Скачать». Навигация — к следующему/предыдущему вложению.
 */
export function FilePreviewModal({ items, index, onClose, onNavigate }: {
    items: PreviewItem[];
    index: number;
    onClose: () => void;
    onNavigate: (index: number) => void;
}) {
    const item = items[index];
    const [data, setData] = useState<Descriptor | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [text, setText] = useState('');
    const [zoomed, setZoomed] = useState(false);
    const [sheet, setSheet] = useState(0);

    useEffect(() => {
        setData(null); setError(null); setText(''); setZoomed(false); setSheet(0);
        if (!item) return;
        let cancelled = false;
        void documents.filePreview(item.blobId).then(async (d) => {
            if (cancelled) return;
            setData(d);
            if (d.kind === 'text' && d.url) {
                try {
                    const resp = await fetch(d.url, { credentials: 'include' });
                    const t = await resp.text();
                    if (!cancelled) setText(t);
                } catch { /* покажем сообщение об ошибке ниже */ }
            }
        }).catch((e) => { if (!cancelled) setError(e); });
        return () => { cancelled = true; };
    }, [item?.blobId]);

    if (!item) return null;
    const inlineUrl = documents.downloadInlineUrl(item.blobId);
    const downloadUrl = `/api/v1/documents/file-blob/${item.blobId}/download/`;
    const hasPrev = index > 0;
    const hasNext = index < items.length - 1;

    let body: React.ReactNode;
    if (error) {
        body = <div className="empty">Не удалось открыть предпросмотр — скачайте файл.</div>;
    } else if (!data) {
        body = <div className="loading">Подготовка просмотра…</div>;
    } else if (data.kind === 'pdf') {
        body = <iframe title={item.name} src={inlineUrl}
                       style={{ width: '100%', height: '70vh', border: 'none' }} />;
    } else if (data.kind === 'image') {
        body = (
            <div style={{ textAlign: 'center', overflow: 'auto', maxHeight: '70vh' }}>
                <img src={inlineUrl} alt={item.name} onClick={() => setZoomed((z) => !z)}
                     style={{ maxWidth: zoomed ? 'none' : '100%', cursor: 'zoom-in' }} />
                <div className="muted" style={{ fontSize: 12 }}>Клик по изображению — масштаб</div>
            </div>
        );
    } else if (data.kind === 'text') {
        body = <pre style={{ whiteSpace: 'pre-wrap', maxHeight: '70vh', overflow: 'auto' }}>
            {text || '…'}
        </pre>;
    } else if (data.kind === 'html') {
        body = (
            <div className="preview-doc" style={{ maxHeight: '70vh', overflow: 'auto' }}
                 dangerouslySetInnerHTML={{ __html: data.html ?? '' }} />
        );
    } else if (data.kind === 'sheets') {
        const sheets = data.sheets ?? [];
        const current = sheets[Math.min(sheet, sheets.length - 1)];
        body = (
            <div>
                {sheets.length > 1 && (
                    <div className="form-actions" style={{ marginBottom: 8 }}>
                        {sheets.map((s, i) => (
                            <button key={s.name || i}
                                    className={`btn small ${i === sheet ? 'primary' : ''}`}
                                    onClick={() => setSheet(i)}>
                                {s.name || `Лист ${i + 1}`}
                            </button>
                        ))}
                    </div>
                )}
                <div className="preview-sheet" style={{ maxHeight: '65vh', overflow: 'auto' }}
                     dangerouslySetInnerHTML={{ __html: current?.html ?? '' }} />
            </div>
        );
    } else {
        body = <div className="empty">
            {(data.kind === 'error' ? data.message : data.reason) || 'Просмотр недоступен.'}
        </div>;
    }

    return (
        <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                    <strong style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.name}
                        {items.length > 1 && <span className="muted"> ({index + 1}/{items.length})</span>}
                    </strong>
                    <button className="btn small" disabled={!hasPrev}
                            onClick={() => onNavigate(index - 1)}>←</button>
                    <button className="btn small" disabled={!hasNext}
                            onClick={() => onNavigate(index + 1)}>→</button>
                    <a className="btn small" href={downloadUrl}>Скачать</a>
                    <button className="btn small" onClick={onClose}>Закрыть</button>
                </div>
                {body}
            </div>
        </div>
    );
}
